import { Col, Container, HeadingThree, maxWidth, Paragraph, Row } from 'hui';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { subscribeToNewsletter } from '../../../actions/newsletter';
import Loader from '../../../components/Loader';
import Constants from '../../../constants/Articles';
import { getNewsletter, getTopicByKey, isTopicLoaded } from '../../../selectors';
import ArticleList from '../ArticleList';
import ArticleSearch from '../ArticleSearch';
import Breadcrumbs from '../Breadcrumbs';
import HeroJumbotron from '../HeroJumbotronContainer';
import NewsletterSubscription from '../NewsletterSubscription';
import Pagination from '../Pagination';
import Share from '../Share';
import { useSetupSeoMetrics } from '../../../utils/snowplow';

const Heading = styled(HeadingThree)`
  ${maxWidth('md')} {
    font-size: 1.25rem;
  }
`;
const Topic = ({ topic, isLoaded, page = 1, perPage = 10, newsletter, subscribeToNewsletter }) => {
  const maxPage = Math.ceil(topic.totalArticles / perPage);
  const nextHref = page < maxPage ? `/c/${topic.key}/page/${page + 1}` : undefined;
  let previousHref = page > 1 ? `/c/${topic.key}/page/${page - 1}` : undefined;

  if (page === 2) {
    previousHref = `/c/${topic.key}`;
  }

  const breadcrumbsData = [
    { name: 'hipages', href: '/' },
    topic.categoryKey
      ? { name: topic.name, href: `/find/${topic.categoryKey}` }
      : { name: 'Articles', href: '/articles' },
    { name: `${topic.name} Articles`, href: `/c/${topic.key}` }
  ];

  const seoMetrics = {
    h1: `${topic.name} Articles`,
    metaDescription: `hipages, a home building and renovation resource featuring contractors directory, house and garden improvement articles`,
    title: `Home Improvement Articles`,
    siteSection: 'Articles',
    siteSubsection: 'Topics'
  };
  useSetupSeoMetrics(seoMetrics);

  if (!isLoaded) return <Loader />;

  return (
    <div>
      <Helmet>
        <title>Home Improvement Articles</title>
        <meta
          name="description"
          content="hipages, a home building and renovation resource featuring contractors directory, house and garden improvement articles"
        />
        <link rel="canonical" href={`https://hipages.com.au/c/${topic.key}`} />
        {previousHref && <link rel="prev" href={`${Constants.BASE_URL}/${previousHref}`} />}
        {nextHref && <link rel="next" href={`${Constants.BASE_URL}/${nextHref}`} />}
      </Helmet>
      <HeroJumbotron
        category={topic.name}
        categoryPerson={topic.categoryPerson}
        categoryPersonPlural={topic.categoryPersonPlural}
        heroImage={topic && topic.articles && topic.articles[0].hero}
      />
      <Breadcrumbs breadcrumbs={breadcrumbsData} />
      <Container mb={4}>
        <Row mt={{ xs: 1, lg: 0 }}>
          <Col xs={12}>
            <Heading as="h1">{topic.name} Articles</Heading>
          </Col>
        </Row>
        <Row>
          <Col xs={12} lg={8}>
            <Fragment>
              {topic.articles.length > 0 && <ArticleList articles={topic.articles} />}
              {topic.articles.length === 0 && (
                <Paragraph align="center">{`Couldn't find any articles`}</Paragraph>
              )}
              <Pagination previousHref={previousHref} nextHref={nextHref} />
            </Fragment>
          </Col>
          <Col xs={12} lg={4}>
            <ArticleSearch />
            <NewsletterSubscription
              subscribeToNewsletter={subscribeToNewsletter}
              subscribed={newsletter.subscribed}
              error={newsletter.error}
            />
            <Share />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

Topic.propTypes = {
  topic: PropTypes.object,
  isLoaded: PropTypes.bool,
  page: PropTypes.number,
  perPage: PropTypes.number,
  newsletter: PropTypes.object,
  subscribeToNewsletter: PropTypes.func
};

export default connect(
  (state, props: any) => {
    const page = props.match.params.page;
    return {
      topic: getTopicByKey(state, props.match.params.topic, page),
      isLoaded: isTopicLoaded(state, props.match.params.topic, page),
      page: page ? Number(page) : 1,
      newsletter: getNewsletter(state)
    };
  },
  { subscribeToNewsletter }
)(Topic);
